import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Button, Empty, Spin, Tag, Typography } from "@douyinfe/semi-ui";
import { IconDeleteStroked } from "@douyinfe/semi-icons";
import Chat from "@douyinfe/semi-ui/lib/es/chat";
import type { Message } from "@douyinfe/semi-foundation/lib/es/chat/foundation";
import {
  AiCoachChatApi,
  ChatMessage,
  streamChat,
} from "../../../api/aiCoachChat";
import { intentionLevelLabel } from "../../../api/aiCoach";
import { displayLeadStatus } from "../../../api/ApiClient";
import {
  aiAvatarBg,
  chatPanelSemi,
  chatWrap,
  panelHeader,
  scoreBadgeColor,
  scoreBadgeStyle,
  sp,
  userAvatarBg,
} from "../styles";

const { Title, Text } = Typography;

interface Props {
  leadId: number | null;
  leadName?: string;
  leadIntention?: string;
  leadStatus?: string;
  leadStar?: number | null;
  leadScore?: number | null;
}

export interface ChatPanelHandle {
  /** 直接发送一条消息（快捷提问用），跳过输入框 */
  send: (text: string) => void;
}

interface LocalMsg {
  id: string;
  role: "user" | "assistant";
  content: string;
  status: "loading" | "incomplete" | "complete" | "error";
  createAt: number;
}

function toMessage(m: LocalMsg): Message {
  return {
    id: m.id,
    role: m.role,
    content: m.content,
    status: m.status,
    createAt: m.createAt,
  };
}

const ChatPanel = forwardRef<ChatPanelHandle, Props>(({
  leadId,
  leadName,
  leadIntention,
  leadStatus,
  leadStar,
  leadScore,
}, ref) => {
  const [messages, setMessages] = useState<LocalMsg[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const closeRef = useRef<(() => void) | null>(null);
  const seqRef = useRef(0);

  useEffect(() => {
    if (leadId == null) return;
    setLoading(true);
    AiCoachChatApi.history(leadId)
      .then((r) => {
        const list = (r.data ?? [])
          .filter((m: ChatMessage) => m.role === "user" || m.role === "assistant")
          .map((m: ChatMessage, i: number) => ({
            id: `hist-${i}`,
            role: m.role as "user" | "assistant",
            content: m.content ?? "",
            status: "complete" as const,
            createAt: Date.now() - i,
          }));
        setMessages(list);
      })
      .finally(() => setLoading(false));
    return () => {
      if (closeRef.current) closeRef.current();
    };
  }, [leadId]);

  const roleConfig = useMemo(
    () => ({
      user: {
        name: "我",
        avatar: (
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              background: userAvatarBg,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            我
          </div>
        ),
      },
      assistant: {
        name: "AI 军师",
        avatar: (
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              background: aiAvatarBg,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 15,
            }}
          >
            ✦
          </div>
        ),
      },
    }),
    []
  );

  const chats = useMemo(() => messages.map(toMessage), [messages]);

  const send = (rawText: string) => {
    const text = rawText.trim();
    if (!leadId || !text || sending) return;

    setInput("");
    setSending(true);

    const userMsg: LocalMsg = {
      id: `u-${++seqRef.current}`,
      role: "user",
      content: text,
      status: "complete",
      createAt: Date.now(),
    };
    const aiMsg: LocalMsg = {
      id: `a-${++seqRef.current}`,
      role: "assistant",
      content: "",
      status: "loading",
      createAt: Date.now() + 1,
    };
    setMessages((prev) => [...prev, userMsg, aiMsg]);

    const close = streamChat(leadId, text, {
      onToken: (delta) => {
        setMessages((prev) => {
          const copy = [...prev];
          const last = copy[copy.length - 1];
          if (last && last.role === "assistant") {
            copy[copy.length - 1] = {
              ...last,
              content: last.content + delta,
              status: "incomplete",
            };
          }
          return copy;
        });
      },
      onDone: () => {
        setMessages((prev) => {
          const copy = [...prev];
          const last = copy[copy.length - 1];
          if (last && last.role === "assistant") {
            copy[copy.length - 1] = {
              ...last,
              status: last.content ? "complete" : "error",
            };
          }
          return copy;
        });
        setSending(false);
      },
      onError: (err) => {
        setMessages((prev) => {
          const copy = [...prev];
          const last = copy[copy.length - 1];
          if (last && last.role === "assistant") {
            copy[copy.length - 1] = {
              ...last,
              content: last.content || `⚠️ 出错：${err}`,
              status: "error",
            };
          }
          return copy;
        });
        setSending(false);
      },
    });
    closeRef.current = close;
  };

  const handleSend = (content: string) => {
    send(content);
  };

  useImperativeHandle(ref, () => ({
    send: (text: string) => send(text),
  }));

  const handleStop = () => {
    if (closeRef.current) {
      closeRef.current();
      closeRef.current = null;
      setSending(false);
      setMessages((prev) => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant" && last.status !== "complete") {
          copy[copy.length - 1] = {
            ...last,
            status: last.content ? "complete" : "error",
          };
        }
        return copy;
      });
    }
  };

  const clearHistory = async () => {
    if (!leadId) return;
    await AiCoachChatApi.clearHistory(leadId);
    setMessages([]);
  };

  if (leadId == null) {
    return (
      <div style={{ ...chatWrap, alignItems: "center", justifyContent: "center" }}>
        <Empty
          image={<div style={{ fontSize: 56 }}>💬</div>}
          description="从左侧选择一位客户，开始与 AI 军师对话"
          style={{ padding: 80 }}
        />
      </div>
    );
  }

  return (
    <div style={chatWrap}>
      <div style={panelHeader}>
        <div style={{ display: "flex", alignItems: "center", gap: sp.sm }}>
          <Title heading={6} style={{ margin: 0 }}>
            {leadName || `客户 #${leadId}`}
          </Title>
          {leadScore != null && (
            <span
              style={{ ...scoreBadgeStyle, background: scoreBadgeColor(leadScore) }}
              title="AI 评分"
            >
              {leadScore}
            </span>
          )}
          {leadStar != null && (
            <Text type="warning" size="small">{leadStar}★</Text>
          )}
          {leadStatus && (
            <Tag size="small" color="blue">{displayLeadStatus(leadStatus)}</Tag>
          )}
          {leadIntention && (
            <Text type="tertiary" size="small">{intentionLevelLabel(leadIntention)}</Text>
          )}
        </div>
        <Button
          size="small"
          theme="borderless"
          icon={<IconDeleteStroked />}
          onClick={clearHistory}
          disabled={sending || messages.length === 0}
        >
          清空对话
        </Button>
      </div>

      {loading ? (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Spin size="large" />
        </div>
      ) : (
        <div style={{ flex: 1, minHeight: 0, padding: sp.sm }}>
          <Chat
            roleConfig={roleConfig}
            chats={chats}
            mode="bubble"
            align="leftRight"
            placeholder={sending ? "AI 军师正在回复…" : "给 AI 军师发消息，Enter 发送，Shift+Enter 换行"}
            showStopGenerate={sending}
            showClearContext={false}
            onMessageSend={handleSend}
            onStopGenerator={handleStop}
            onInputChange={(p) => setInput(p.value ?? "")}
            canSend={!sending && input.trim().length > 0}
            style={chatPanelSemi}
            escapeHtml={false}
          />
        </div>
      )}
    </div>
  );
});

export default ChatPanel;
